import { memo, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  IconButton,
  Tooltip,
  IconButtonProps,
  TooltipProps,
  MenuItem,
  ListItemIcon,
} from '@mui/material';

type OpenInNewButtonProps = Omit<IconButtonProps, 'children'> & {
  href: string;
  tooltip?: string;
  tooltipPlacement?: TooltipProps['placement'];
  onIconClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  sx?: IconButtonProps['sx'];
  asMenuItem?: boolean;
  testId?: string;
};

export const OpenInNewButton = memo(
  ({
    href,
    tooltip,
    tooltipPlacement = 'left',
    onIconClick,
    sx,
    asMenuItem = false,
    testId,
    ...iconButtonProps
  }: OpenInNewButtonProps) => {
    const { t } = useTranslation();
    const label = tooltip || t('blox.actions.open_in_new_tab');
    const dataTestId = testId ? `open-in-new-${testId}` : undefined;

    if (asMenuItem) {
      return (
        <MenuItem
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={dataTestId}
          disabled={iconButtonProps.disabled}
          onClick={(e: MouseEvent<HTMLAnchorElement>) => {
            e.stopPropagation();
            onIconClick?.(e);
          }}
          sx={{
            gap: 1,
            '& .MuiListItemIcon-root': { minWidth: 'auto' },
          }}
        >
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          {label}
        </MenuItem>
      );
    }

    return (
      <Tooltip
        title={label}
        placement={tooltipPlacement}
        disableInteractive
      >
        <IconButton
          aria-label="open-in-new"
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          data-testid={dataTestId}
          onClick={(e: MouseEvent<HTMLAnchorElement>) => {
            e.stopPropagation();
            onIconClick?.(e);
          }}
          sx={{ p: 0.5, ...sx }}
          {...iconButtonProps}
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  },
);
OpenInNewButton.displayName = 'OpenInNewButton';
